<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, watch, onMounted, useTemplateRef, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import CreateAccessTokenModal from '@/components/modals/CreateAccessTokenModal.vue'
import EditAccessTokenModal from '@/components/modals/EditAccessTokenModal.vue'
import DeleteAccessTokenModal from '@/components/modals/DeleteAccessTokenModal.vue'
import AccessTokenService from '@/services/AccessTokenService'
import FormButton from '../../components/Atoms/FormButton.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import IconBase from '../../components/icon/IconBase.vue'
import IconBin from '../../components/icon/IconBin.vue'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const { notify } = useNotification()
const store = useStore()

// Refs
const error = ref(null)
const processing = ref(false)
const accessTokens = ref([])
const accessToken = ref({})
const fields = ref([
  { key: 'name', label: 'Token Name', class: 'text-s-s' },
  { key: 'accessId', label: 'Access ID', class: 'text-s-s' },
  { key: 'creationTime', label: 'Created', class: 'text-s-s' },
  { key: 'actions', label: '' }
])

// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const subscriptions = computed(() => store.getters['User/subscriptions'])
const v2ApiKey = computed(() => store.getters['User/v2ApiKey'])
const canAccessDevelopers = computed(() => store.getters['User/canAccessDevelopers'])
const userEmail = computed(() => store.getters['User/userEmail'])
const isValidPlan = computed(() => {
  // TODO below might be the list of legacy user subscriptions.
  // Maybe we can delete it
  // const validPlans = [
  //   '602cc8de03afd67aa9831cf1',
  //   '602cc8a503afd67aa9831cf0',
  //   '602cc81503afd67aa9831cee',
  //   '602cc85e03afd67aa9831cef',
  //   '6361ecd609950a6a642540be',
  //   '6361ecd609950a6a642540bd',
  //   '6361ecd609950a6a642540bb',
  //   '6361ecd609950a6a642540bc',
  //   '6435028a40771d569f1e98ee',
  //   '6435041040771d569f1e98f8',
  //   '643502dd40771d569f1e98f0',
  //   '6435044340771d569f1e98fa'
  // ]
  const AIOPlan = subscriptions.value.find(item => item.enabled && item.category === 'live')
  return AIOPlan && v2ApiKey.value
})

// Hooks
// Member permissions can resolve after mount, so load once access is granted.
watch(canAccessDevelopers, async (allowed) => {
  if (allowed) accessTokens.value = await AccessTokenService.getAccessTokens()
}, { immediate: true })

// Methods
const onInputChange = (prop) => {
  formErrors.value[prop] = false
}

const createToken = () => {
  showModal('modalCreateAccessToken')
}
const editAccessToken = (token) => {
  accessToken.value = token
  showModal('modalEditAccessToken')
}
const deleteAccessToken = (token) => {
  accessToken.value = token
  showModal('modalDeleteAccessToken')
}
const onTokenDismiss = () => {
  accessToken.value = {}
}
const onTokenUpdate = async () => {
  accessTokens.value = await AccessTokenService.getAccessTokens()
}

</script>

<template>
  <div class="view">
    <main>
      <section class="text-center mt-6 lg:mt-12" v-if="!canAccessDevelopers">
        <h4 class="text-surface-9 text-lg font-medium">You don’t have permission to access this.</h4>
        <p class="text-surface-6 text-base">Contact the team owner at <a class="text-surface-6 underline"
            :href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</p>
      </section>

      <section class="text-center mt-6 lg:mt-32" v-else-if="!isValidPlan">
        <h2 class="text-lg text-surface-9 font-medium mb-2">Upgrade required</h2>
        <p class="text-sm text-surface-7 mb-4">API Access Tokens are not available in your
          current plan. Please <a class="underline text-surface-7" target="_blank" href="/app/subscribe">upgrade</a>
          to enable token generation.</p>
        <FormButton to="/subscribe">
          Upgrade now
        </FormButton>
      </section>

      <section v-else>
        <p class="text-s-s text-dark-7 text-normal mb-4">
          Access tokens are used to authenticate API requests. Tokens are environment-specific and can have different
          permissions based on the product you’re using. Learn more in our guide.
        </p>
        <p class="flex mb-6">
          <Tooltip :active="accessTokens.length"
            content="Only one access token can be created at a time. Please delete the existing token access to generate a new one.">
            <FormButton @click="createToken" :disabled="accessTokens.length">Create API
              Token
            </FormButton>
          </Tooltip>
        </p>
        <div class="max-w-full% overflow-x-auto lg:overflow-x-visible">
          <table class="w-full" :busy="loading">
            <thead class="!border-b !border-b-surface-3">
              <th class="font-normal text-left text-sm text-surface-6 py-2" v-for="(field, index) in fields" :key="index">
                {{ field.label }}
              </th>
            </thead>
            <tbody class="divide-y divide-surface-3 text-sm text-surface-9">
              <tr v-for="token in accessTokens">
                <td class="py-2">
                  {{ token.name }}
                </td>
                <td class="py-2">
                  {{ token.accessId }}
                </td>
                <td class="py-2">
                  {{ token.creationTime }}
                </td>
                <td class="text-right py-2">
                  <div class="flex gap-2 items-center justify-end">
                    <FormButton @click="editAccessToken(token)" size="sm" type="secondary">
                      Edit
                    </FormButton>
                    <FormButton @click="deleteAccessToken(token)" isIcon size="sm" type="danger">
                      <IconBase>
                        <IconBin />
                      </IconBase>
                    </FormButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
  <create-access-token-modal @token-created="onTokenUpdate" />
  <edit-access-token-modal :access-token="accessToken" @token-dismiss="onTokenDismiss" @token-edited="onTokenUpdate" />
  <delete-access-token-modal :access-token="accessToken" @token-deleted="onTokenUpdate" />
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
